import { Link } from "react-router-dom";
import { Award, Heart, Users, Compass, ArrowRight, CheckCircle2, GraduationCap, Briefcase, Sparkles, Quote } from "lucide-react";
import { NAVICA, IMAGERY } from "@/lib/constants";
import PartnersStrip from "@/components/sections/PartnersStrip";
import { NavicaSeal, CulturalFlagsStrip } from "@/components/brand/NavicaBrand";
import usePageMeta from "@/hooks/usePageMeta";

export default function About() {
  usePageMeta({
    title: "About Madelyn & Navica Supports, AASW Social Worker Sydney",
    description: "Meet the AASW-accredited social worker behind Navica Supports. Six years in the NDIS sector supporting participants across Sydney with case management, recovery coaching, and specialist coordination.",
  });
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-16 lg:pt-24 pb-16 overflow-hidden" data-testid="about-hero">
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-5 fade-up">About Navica Supports</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-[#1F2A2E] mb-8 fade-up fade-up-1">
            Navigating care <span className="italic" style={{ color: "var(--navica-coral)" }}>with YOU.</span>
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl fade-up fade-up-2">
            Navica Supports is an AASW aligned NDIS practice based in Sydney. We exist to make complex systems feel a little less overwhelming, and to make sure people aren't navigating them alone.
          </p>
        </div>
      </section>

      {/* DIRECTOR feature block (no staff photo, typographic + symbolic) */}
      <section className="py-20 lg:py-28" data-testid="director-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">
          {/* Left: quote card + credential blocks (no photo) */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative">
              {/* Editorial quote block replacing photo */}
              <div className="bg-[#1A363D] text-white rounded-sm p-10 lg:p-12">
                <Quote className="w-10 h-10 text-[#EA823A] mb-6" />
                <div className="font-display text-2xl md:text-3xl leading-snug italic">
                  &ldquo;Enabling people to live fulfilling lives, feeling valued and supported throughout their journey.&rdquo;
                </div>
                <div className="text-sm text-[#F4C3CC] mt-6 not-italic tracking-wider uppercase">A word from our Director</div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="bg-[#EA823A] text-white rounded-sm px-5 py-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-80 mb-1">Leadership</div>
                  <div className="text-sm font-semibold leading-tight">Director · Navica Supports Pty Ltd</div>
                </div>
                <div className="bg-white border border-[#284952]/10 rounded-sm px-5 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <GraduationCap className="w-4 h-4 text-[#E2718A]" />
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B7A80]">Qualified</span>
                  </div>
                  <div className="text-sm font-semibold text-[#1F2A2E] leading-snug">BSW · AASW</div>
                  <div className="text-xs text-[#6B7A80] mt-1">#{NAVICA.aasw_number}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: bio */}
          <div className="lg:col-span-7">
            <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-4">A word from our Director</div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#1F2A2E] leading-tight mb-8">
              People first. Always.
            </h2>

            <div className="space-y-6 text-lg text-[#4A595E] leading-relaxed">
              <p>
                As Director at <span className="font-semibold text-[#1F2A2E]">Navica Supports Pty Ltd</span>, I lead our experienced team in delivering high quality support coordination services to NDIS participants. Our focus is on ensuring access to the right services and fostering connections that empower individuals to achieve their goals with a person centred approach.
              </p>
              <p>
                With <span className="font-semibold text-[#1F2A2E]">six years of experience in the NDIS sector</span> in addition to the completion of a Bachelor's degree in Social Work, I bring expertise in leadership, high quality care, and collaborative problem solving to my role.
              </p>
              <p>
                My commitment lies in enabling individuals to live fulfilling lives while feeling valued and supported throughout their journey.
              </p>
            </div>

            {/* Credentials chips */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                { icon: GraduationCap, label: "Bachelor of Social Work" },
                { icon: Award, label: "AASW aligned practice" },
                { icon: Briefcase, label: "6 years in the NDIS sector" },
                { icon: Users, label: "Leadership and team led care" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-3 bg-[#F1E4D5] rounded-2xl px-4 py-3 border border-[#F0E5DE]">
                  <c.icon className="w-5 h-5 text-[#E2718A] shrink-0" />
                  <span className="text-sm font-medium text-[#284952]">{c.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-[#F0E5DE] flex items-end justify-between gap-6 flex-wrap">
              <div>
                <SignatureMadelyn />
                <div className="font-display italic text-xl text-[#E2718A] mt-1">{NAVICA.director}</div>
                <div className="text-sm text-[#6B7A80]">{NAVICA.director_title}</div>
                <div className="text-xs text-[#6B7A80] mt-0.5">AASW #{NAVICA.aasw_number}</div>
              </div>
              <NavicaSeal size={120} className="opacity-90 shrink-0" />
            </div>
          </div>
        </div>
      </section>

      {/* CULTURAL SAFETY, Acknowledgement of Country */}
      <section className="py-16 bg-[#FAF1E4] border-y border-[#F0E5DE]" data-testid="cultural-safety-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-4 flex items-center gap-3 justify-center">
            <span className="w-8 h-px bg-[#EA823A]/50" />
            Acknowledgement of Country
            <span className="w-8 h-px bg-[#EA823A]/50" />
          </div>
          <div className="text-center text-base md:text-lg text-[#284952] leading-relaxed space-y-5 max-w-3xl mx-auto font-sans">
            <p>
              Navica Supports acknowledges the <span className="italic">Dharug people</span>, the Traditional Custodians of the land on which we live, work and gather in Parramatta. We pay our deepest respects to Elders past, present and emerging, and honour the enduring connection Aboriginal and Torres Strait Islander peoples hold to Country, waterways, culture and community.
            </p>
            <p className="text-[#E2718A] italic font-semibold">
              Sovereignty was never ceded. This always was, and always will be, Aboriginal land.
            </p>
            <p>
              We commit to walking alongside, never ahead of, First Nations communities. We listen more than we speak, we are informed by direct Aboriginal and Torres Strait Islander insights, and we let First Nations wisdom, self-determination and lived experience shape our practice. We recognise the strength, resilience and achievements of Aboriginal and Torres Strait Islander peoples, past, present and emerging.
            </p>
            <p className="text-base text-[#6B7A80] italic">
              Navica is a safe and affirming space for Sistergirls, Brotherboys and LGBTIQA+ First Nations peoples. Navica is an ally of the LGBTIQA+ community, we pride ourselves on our inclusive approach, where judgement does not exist, and we welcome all people who identify as part of the LGBTIQA+ community.
            </p>
          </div>
          <div className="mt-8 flex justify-center">
            <CulturalFlagsStrip showLabels={false} />
          </div>
        </div>
      </section>

      {/* MISSION + VALUES */}
      <section className="py-20 lg:py-24 bg-[#F1E4D5]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-sm uppercase tracking-[0.2em] text-[#E2718A] font-semibold mb-4">Our promise</div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#1F2A2E] leading-tight mb-6">
              Person centred. Strengths based. Informed by lived experience.
            </h2>
            <p className="text-lg text-[#4A595E] leading-relaxed mb-8">
              Every plan starts with listening. Every relationship is grounded in dignity. Our experienced team holds the AASW Code of Ethics at the centre of practice, alongside six years of frontline NDIS experience.
            </p>
            <Link to="/services" className="btn-primary" data-testid="about-cta-services">
              Explore our services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Heart, title: "Compassion first", body: "We meet people where they are, with warmth, patience and without judgement." },
              { icon: Users, title: "Person centred", body: "You set the goals. We bring the maps, the connections and the steady company." },
              { icon: Award, title: "Quality and accountable", body: "AASW aligned practice, NDIS Quality and Safeguards standards, six years' experience." },
              { icon: Compass, title: "Straight talking", body: "No jargon. Just clear, actionable next steps that respect your time and energy." },
            ].map((v, idx) => (
              <div key={v.title} className="bg-white rounded-3xl p-7 border border-[#F0E5DE] fade-up" style={{ animationDelay: `${idx * 0.08}s` }} data-testid={`value-${idx}`}>
                <div className="w-12 h-12 rounded-xl bg-[#FCE7EB] flex items-center justify-center mb-5">
                  <v.icon className="w-5 h-5 text-[#E2718A]" />
                </div>
                <h3 className="font-display text-xl text-[#1F2A2E] mb-3">{v.title}</h3>
                <p className="text-sm text-[#4A595E] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS STRIP */}
      <PartnersStrip />

      {/* GROWING SCOPE */}
      <section className="py-24" data-testid="scope-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <Sparkles className="w-8 h-8 text-[#EA823A] mx-auto mb-5" />
          <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-4">Growing our scope</div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#1F2A2E] leading-tight mb-7">
            From NDIS coordination, into the spaces where great care has been missing.
          </h2>
          <p className="text-lg text-[#4A595E] leading-relaxed mb-10 max-w-3xl mx-auto">
            In 2026, our practice is intentionally expanding into specialist domains where high quality, accredited social work support is too often hard to find. Department of Veterans' Affairs navigation. Family and domestic violence response. The unique mental health and rehabilitation needs of athletes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {["NDIS Support Coordination", "Case Management", "Psychosocial Recovery", "DVA", "Family Violence Support", "Athlete Mental Health"].map((s) => (
              <span key={s} className="px-4 py-2 rounded-full bg-[#F1E4D5] border border-[#F0E5DE] text-sm font-medium text-[#284952]">
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES BAND */}
      <section className="py-20 bg-[#284952] text-white relative overflow-hidden" data-testid="principles-band">
        <div className="absolute inset-0 opacity-10 grain" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-sm uppercase tracking-[0.2em] text-[#F4C3CC] font-semibold mb-4">What guides us</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight mb-12">
            The work is the <span className="italic text-[#EA823A]">relationship</span>.
          </h2>
          <div className="grid md:grid-cols-3 gap-10 text-left">
            {[
              { n: "01", t: "Dignity", b: "Every interaction protects autonomy and self determination." },
              { n: "02", t: "Transparency", b: "Clear language. No hidden steps. You always know what's happening." },
              { n: "03", t: "Outcomes", b: "We measure success in the milestones you choose, the fulfilling life you're building." },
            ].map((p) => (
              <div key={p.n}>
                <div className="font-display text-4xl text-[#EA823A] mb-3">{p.n}</div>
                <h3 className="font-display text-xl mb-2">{p.t}</h3>
                <p className="text-sm text-white/80 leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="about-cta">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-[#1F2A2E] leading-tight mb-6">
            Want to chat first?
          </h2>
          <p className="text-lg text-[#4A595E] leading-relaxed mb-8">
            A no obligation conversation is often the best first step. Call us, email us, or send a referral. We'll take it from there.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/contact" className="btn-primary">Get in touch <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/referrals" className="btn-outline">Make a referral</Link>
          </div>
        </div>
      </section>
    </div>
  );
}


/**
 * Handwritten-style signature for Madelyn Agius. Pure SVG, no external assets.
 * Uses a script font stack with a fallback italic serif so it always renders.
 */
function SignatureMadelyn() {
  return (
    <svg width="320" height="70" viewBox="0 0 320 70" role="img" aria-label="Madelyn Agius signature" className="text-[#284952]">
      <text
        x="0" y="48"
        fontFamily="'Great Vibes', 'Snell Roundhand', 'Apple Chancery', 'Segoe Script', cursive"
        fontSize="46" fill="currentColor"
      >
        Madelyn Agius
      </text>
      <line x1="4" y1="58" x2="300" y2="58" stroke="currentColor" strokeWidth="0.7" opacity="0.25" />
    </svg>
  );
}
